import { API_BASE_URL, TOKEN_STORAGE_KEY } from '../config/index.js';

/**
 * Thin fetch wrapper around the backend API.
 *
 * - Attaches the stored Bearer token automatically.
 * - Unwraps the backend's `{ data }` envelope on success.
 * - Throws an Error carrying { status, details } on failure, so callers can
 *   `try/catch` and show err.message.
 */
class ApiService {
  /**
   * @param {string} endpoint
   * @param {{ method?: string, body?: any, headers?: object, timeoutMs?: number }} [options]
   *   `timeoutMs`, when set, wraps the call in a real `AbortController` timeout
   *   (never a UI-only fake-delay demo) — the request is genuinely aborted if
   *   the server hasn't responded within that window. On abort, the thrown
   *   Error carries `isTimeout: true` so callers can show a specific message
   *   (e.g. the Copilot panel's TC-39219-02 fallback) instead of a generic one.
   */
  /**
   * Fetch a binary response (a download) rather than JSON.
   *
   * Kept separate from `request` because the auth header is what forces it to
   * exist at all: an <a href> to an API route cannot carry a Bearer token, so a
   * sized download has to be fetched and handed to the browser as a blob.
   */
  async blob(endpoint) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (!res.ok) {
      const payload = await res.json().catch(() => null);
      const error = new Error(payload?.error?.message || `Download failed (${res.status})`);
      error.status = res.status;
      throw error;
    }
    const disposition = res.headers.get('content-disposition') || '';
    const filename = /filename="([^"]+)"/.exec(disposition)?.[1] || null;
    return { blob: await res.blob(), filename };
  }

  async request(endpoint, { method = 'GET', body, headers, timeoutMs } = {}) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    // A FormData body (e.g. a multipart file upload) must NOT be JSON-encoded
    // and must NOT carry an explicit Content-Type — the browser sets its own
    // `multipart/form-data; boundary=...` header, which we'd otherwise clobber.
    const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

    const controller = timeoutMs ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;

    let res;
    try {
      res = await fetch(`${API_BASE_URL}${endpoint}`, {
        method,
        headers: {
          ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...headers,
        },
        ...(body ? { body: isFormData ? body : JSON.stringify(body) } : {}),
        ...(controller ? { signal: controller.signal } : {}),
      });
    } catch (err) {
      if (err?.name === 'AbortError') {
        const timeoutError = new Error(`Request timed out after ${timeoutMs}ms`);
        timeoutError.isTimeout = true;
        throw timeoutError;
      }
      throw err;
    } finally {
      if (timer) clearTimeout(timer);
    }

    const isJson = res.headers.get('content-type')?.includes('application/json');
    const payload = isJson ? await res.json() : null;

    if (!res.ok) {
      const message = payload?.error?.message || `Request failed (${res.status})`;
      const error = new Error(message);
      error.status = res.status;
      error.details = payload?.error?.details;
      throw error;
    }

    return payload?.data ?? payload;
  }

  /**
   * PUT one binary chunk with upload-progress events (fetch cannot report
   * upload progress, so this uses XMLHttpRequest). Same base URL and Bearer
   * token source as `request`; resolves `payload.data ?? payload`.
   *
   * @param {string} endpoint
   * @param {Blob} blob
   * A stalled chunk fails after `timeoutMs` (status 0, so the caller retries),
   * and aborting `signal` cancels the request (rejects `{ cancelled: true }`).
   *
   * @param {{ onProgress?: (loadedBytes: number) => void, signal?: AbortSignal, timeoutMs?: number }} [options]
   */
  uploadChunk(endpoint, blob, { onProgress, signal, timeoutMs = 120000 } = {}) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    return new Promise((resolve, reject) => {
      const xhr = new window.XMLHttpRequest();
      xhr.open('PUT', `${API_BASE_URL}${endpoint}`);
      xhr.timeout = timeoutMs;
      xhr.setRequestHeader('Content-Type', 'application/octet-stream');
      const cancelled = () => Object.assign(new Error('Upload cancelled'), { cancelled: true });
      const onAbortSignal = () => xhr.abort();
      if (signal) {
        if (signal.aborted) {
          reject(cancelled());
          return;
        }
        signal.addEventListener('abort', onAbortSignal, { once: true });
      }
      const cleanup = () => signal?.removeEventListener('abort', onAbortSignal);
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      if (onProgress && xhr.upload) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) onProgress(e.loaded);
        };
      }
      xhr.onload = () => {
        cleanup();
        let payload = null;
        try {
          payload = xhr.responseText ? JSON.parse(xhr.responseText) : null;
        } catch {
          payload = null;
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(payload?.data ?? payload);
          return;
        }
        const error = new Error(payload?.error?.message || `Request failed (${xhr.status})`);
        error.status = xhr.status;
        error.details = payload?.error?.details;
        reject(error);
      };
      xhr.onerror = () => {
        cleanup();
        const error = new Error('Network error while uploading');
        error.status = 0;
        reject(error);
      };
      xhr.ontimeout = () => {
        cleanup();
        const error = new Error('Upload timed out');
        error.status = 0;
        reject(error);
      };
      xhr.onabort = () => {
        cleanup();
        reject(cancelled());
      };
      xhr.send(blob);
    });
  }

  // ---- Auth ----
  register({ email, name, password }) {
    return this.request('/auth/register', { method: 'POST', body: { email, name, password } });
  }

  login({ email, password }) {
    return this.request('/auth/login', { method: 'POST', body: { email, password } });
  }

  me() {
    return this.request('/auth/me');
  }

  // ---- Users ----
  listUsers({ limit = 50, offset = 0 } = {}) {
    return this.request(`/users?limit=${limit}&offset=${offset}`);
  }
}

export default new ApiService();
